import { useState, useMemo } from 'react';
import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import { Input } from '@/components/ui/input';
import { useDIDs } from '../hooks';
import { useDIDStore } from '../stores/did-store';
import type { DID } from '../types';
import { formatPhoneNumber } from '@/lib/utils';
import {
  Search,
  TriangleAlert,
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';

interface E911ComplianceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

// Format E911 address for display
function formatE911Address(did: DID): string {
  const parts = [
    did.e911_address1,
    did.e911_address2,
    did.e911_city,
    did.e911_state,
    did.e911_zip,
  ].filter(Boolean);
  return parts.join(', ') || 'No address';
}

// Section header component
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  color: 'amber' | 'green' | 'slate';
  isExpanded: boolean;
  onToggle: () => void;
  defaultExpanded?: boolean;
}

function SectionHeader({
  icon,
  title,
  count,
  color,
  isExpanded,
  onToggle,
}: SectionHeaderProps) {
  const colorClasses = {
    amber: 'border-l-amber-400 bg-amber-50/50',
    green: 'border-l-green-400 bg-green-50/50',
    slate: 'border-l-slate-300 bg-slate-50/50',
  };

  const textColors = {
    amber: 'text-amber-700',
    green: 'text-green-700',
    slate: 'text-slate-500',
  };

  return (
    <button
      onClick={onToggle}
      className={`w-full px-4 py-3 flex items-center justify-between border-l-4 ${colorClasses[color]} hover:bg-opacity-75 transition-colors`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className={`text-sm font-medium ${textColors[color]}`}>{title}</span>
        <span className={`text-xs ${textColors[color]} opacity-75`}>({count})</span>
      </div>
      {isExpanded ? (
        <ChevronDown size={16} className="text-slate-400" />
      ) : (
        <ChevronRight size={16} className="text-slate-400" />
      )}
    </button>
  );
}

// DID row component
interface DIDRowProps {
  did: DID;
  variant: 'missing' | 'compliant' | 'no-e911';
  onSelect: () => void;
  index: number;
}

function DIDRow({ did, variant, onSelect, index }: DIDRowProps) {
  const variantStyles = {
    missing: 'hover:bg-amber-50',
    compliant: 'hover:bg-green-50',
    'no-e911': 'hover:bg-slate-50',
  };

  return (
    <div
      onClick={onSelect}
      className={`px-4 py-3 flex items-center justify-between cursor-pointer ${variantStyles[variant]} border-b border-slate-100 animate-fade-in`}
      style={{ animationDelay: `${Math.min(index * 20, 300)}ms` }}
    >
      <div className="flex-1 min-w-0">
        <span className="font-mono-data text-sm text-indigo-600 font-medium">
          {formatPhoneNumber(did.tn)}
        </span>
        {variant !== 'no-e911' && (
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {formatE911Address(did)}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 ml-4">
        {variant === 'missing' && (
          <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
            Add Alert →
          </span>
        )}
        {variant === 'no-e911' && (
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
            Configure →
          </span>
        )}
        <ChevronRight size={14} className="text-slate-400" />
      </div>
    </div>
  );
}

export function E911ComplianceDrawer({ isOpen, onClose }: E911ComplianceDrawerProps) {
  const { data: dids = [] } = useDIDs();
  const { openDetailModal } = useDIDStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    missing: true, // Always expanded by default
    compliant: false, // Collapsed by default
    noE911: false, // Collapsed by default
  });

  // Categorize DIDs
  const categorized = useMemo(() => {
    const missing: DID[] = [];
    const compliant: DID[] = [];
    const noE911: DID[] = [];

    dids.forEach((did: DID) => {
      if (!did.has_911) {
        noE911.push(did);
      } else if (!did.e911_has_alerts) {
        missing.push(did);
      } else {
        compliant.push(did);
      }
    });

    return { missing, compliant, noE911 };
  }, [dids]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return categorized;

    const normalizedSearch = searchTerm.replace(/\D/g, '');
    const filter = (did: DID) =>
      did.tn.includes(normalizedSearch) ||
      did.e911_address1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      did.e911_city?.toLowerCase().includes(searchTerm.toLowerCase());

    return {
      missing: categorized.missing.filter(filter),
      compliant: categorized.compliant.filter(filter),
      noE911: categorized.noE911.filter(filter),
    };
  }, [categorized, searchTerm]);

  const toggleSection = (section: 'missing' | 'compliant' | 'noE911') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSelectDID = (tn: string) => {
    // Open the DID detail drawer to the 911 alerts tab
    openDetailModal(tn, 'e911');
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const hasActionRequired = categorized.missing.length > 0;

  return (
    <SlideOverDrawer isOpen={isOpen} onClose={handleClose} width="md">
      <DrawerHeader
        title="E911 Alert Compliance"
        onClose={handleClose}
        badge={
          hasActionRequired ? (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
              Action Required
            </span>
          ) : undefined
        }
      />

      <DrawerBody className="flex flex-col">
        {/* Missing E911 Notifications Warning Banner */}
        <div className="p-4 border-b border-slate-200">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <TriangleAlert className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Missing E911 Alert Notifications
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Numbers with active E911 service require at least one notification endpoint
                configured. This is a regulatory requirement.
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              placeholder="Search by number or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Grouped Lists */}
        <div className="flex-1 overflow-y-auto">
          {/* Section 1: Missing Notifications (Amber) */}
          {filtered.missing.length > 0 && (
            <div>
              <SectionHeader
                icon={<TriangleAlert size={16} className="text-amber-500" />}
                title="MISSING NOTIFICATIONS"
                count={filtered.missing.length}
                color="amber"
                isExpanded={expandedSections.missing}
                onToggle={() => toggleSection('missing')}
              />
              {expandedSections.missing && (
                <div>
                  {filtered.missing.map((did, index) => (
                    <DIDRow
                      key={did.tn}
                      did={did}
                      variant="missing"
                      onSelect={() => handleSelectDID(did.tn)}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Section 2: Fully Compliant (Green) */}
          {filtered.compliant.length > 0 && (
            <div>
              <SectionHeader
                icon={<CheckCircle2 size={16} className="text-green-500" />}
                title="FULLY COMPLIANT"
                count={filtered.compliant.length}
                color="green"
                isExpanded={expandedSections.compliant}
                onToggle={() => toggleSection('compliant')}
              />
              {expandedSections.compliant && (
                <div>
                  {filtered.compliant.map((did, index) => (
                    <DIDRow
                      key={did.tn}
                      did={did}
                      variant="compliant"
                      onSelect={() => handleSelectDID(did.tn)}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Section 3: No E911 (Slate) */}
          {filtered.noE911.length > 0 && (
            <div>
              <SectionHeader
                icon={<Circle size={16} className="text-slate-400" />}
                title="NO E911 CONFIGURED"
                count={filtered.noE911.length}
                color="slate"
                isExpanded={expandedSections.noE911}
                onToggle={() => toggleSection('noE911')}
              />
              {expandedSections.noE911 && (
                <div>
                  {filtered.noE911.map((did, index) => (
                    <DIDRow
                      key={did.tn}
                      did={did}
                      variant="no-e911"
                      onSelect={() => handleSelectDID(did.tn)}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {filtered.missing.length === 0 &&
            filtered.compliant.length === 0 &&
            filtered.noE911.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                {searchTerm.trim()
                  ? 'No numbers match your search'
                  : 'No DIDs in your inventory'}
              </div>
            )}
        </div>
      </DrawerBody>
    </SlideOverDrawer>
  );
}
